import { observer } from "mobx-react-lite";
import { Link } from "react-router-dom";
import {
  IconArrowLeft,
  IconBox,
  IconChevronRight,
  IconComponents,
  IconReportMoney,
  IconStack2,
  type TablerIconsProps,
} from "@tabler/icons-react";
import type { ComponentType } from "react";
import { useEnv } from "src/contexts/env.context";
import { useFeatureFlag } from "src/contexts/posthog.context";
import { FLAGS } from "src/posthog/flags";

// Central landing page for the admin / diagnostic screens. These tools were
// previously reachable only by typing the URL — this gives stakeholders one
// place to discover and navigate them. Each entry mirrors the gating applied
// to its route in Router.tsx: `flag` hides a tool until its PostHog flag is on,
// `devOnly` hides it outside local/dev mode.

type AdminTool = {
  title: string;
  description: string;
  to: string;
  Icon: ComponentType<TablerIconsProps>;
  group: string;
  flag?: (typeof FLAGS)[keyof typeof FLAGS];
  devOnly?: boolean;
};

const ADMIN_TOOLS: AdminTool[] = [
  {
    group: "Cost",
    title: "Cost tracking",
    description:
      "Re-cost a fixed set of building variants against the current cost data to catch regressions.",
    to: "/admin/cost-tracking",
    Icon: IconReportMoney,
  },
  {
    group: "Building library",
    title: "Module explorer",
    description:
      "See how each apartment module is shaped and how the modules tessellate into a building.",
    to: "/admin/module-explorer",
    Icon: IconBox,
  },
  {
    group: "Building library",
    title: "Component library",
    description:
      "The kit of parts below a module — walls, floors, doors, windows — with full spec sheets.",
    to: "/admin/component-explorer",
    Icon: IconComponents,
  },
  {
    group: "Pipeline",
    title: "Tessellation steps",
    description: "Walk the aggregator pipeline stage by stage for a given brief.",
    to: "/admin/tessellation-steps",
    Icon: IconStack2,
    flag: FLAGS.tessellationSteps,
  },
];

export const AdminScreen = observer(function AdminScreen() {
  const env = useEnv();
  const isDev = env.authMode !== "prod";
  const tessellationStepsEnabled = useFeatureFlag(FLAGS.tessellationSteps);

  const visible = ADMIN_TOOLS.filter((tool) => {
    if (tool.devOnly && !isDev) return false;
    if (tool.flag === FLAGS.tessellationSteps && !tessellationStepsEnabled) return false;
    return true;
  });

  // Preserve the declared order of groups while collapsing the flat list.
  const groups: { name: string; tools: AdminTool[] }[] = [];
  for (const tool of visible) {
    let group = groups.find((g) => g.name === tool.group);
    if (!group) {
      group = { name: tool.group, tools: [] };
      groups.push(group);
    }
    group.tools.push(tool);
  }

  return (
    <div className="w-screen h-screen overflow-y-auto bg-neutral-2">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <Link
          to="/home"
          className="inline-flex items-center gap-1.5 text-body-1 text-neutral-6 hover:text-primary-7 mb-6"
        >
          <IconArrowLeft className="w-4 h-4" />
          Back to projects
        </Link>

        <header className="mb-8">
          <h1 className="text-header-3 text-neutral-8">Admin</h1>
          <p className="text-body-1 text-neutral-6 mt-1">
            Internal tools for inspecting cost data, the building kit of parts, and the
            tessellation pipeline.
          </p>
        </header>

        <div className="space-y-8">
          {groups.map((group) => (
            <section key={group.name}>
              <h2 className="text-overline uppercase tracking-wide text-neutral-6 mb-3">
                {group.name}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {group.tools.map((tool) => (
                  <ToolCard key={tool.to} tool={tool} />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
});

const ToolCard = ({ tool }: { tool: AdminTool }) => {
  const { Icon } = tool;
  return (
    <Link
      to={tool.to}
      className="group flex flex-col bg-white rounded border border-neutral-4 p-4
      hover:shadow-md hover:shadow-elevation-3 hover:border-primary-5 transition-shadow"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center justify-center w-9 h-9 rounded bg-primary-2 text-primary-7">
          <Icon className="w-5 h-5" />
        </div>
        <IconChevronRight className="w-4 h-4 text-neutral-5 group-hover:text-primary-7" />
      </div>
      <p className="text-subtitle-2 text-neutral-8">{tool.title}</p>
      <p className="text-caption text-neutral-6 mt-1">{tool.description}</p>
    </Link>
  );
};
